import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { FaArrowDown, FaArrowUp, FaPlus, FaSearch, FaTrash } from 'react-icons/fa';
import moment from 'moment-timezone';
import { useNavigate } from 'react-router-dom';

import AvatarCropper from './AvatarCropper';
import './Settings.css';
import {
  fetchUserSettings,
  saveAvatar,
  saveCelebrations,
  saveIntClocks,
  saveSessionLength,
  saveTimezone,
  saveWeatherLocation,
  searchCities,
} from './settingsApi';
import { createEmptyValidationState, validateSettings } from './settingsValidation';

const TIMEZONE_OPTIONS = moment.tz.names();

const SECTION_LABELS = {
  timezone: 'Timezone',
  weather: 'Weather location',
  sessionLength: 'Session length',
  intClocks: 'International clocks',
  celebrations: 'Celebrations',
  avatar: 'Avatar',
};

const createDefaultSettingsState = () => ({
  timezone: '',
  weather: {
    cityQuery: '',
    cityLabel: '',
    lat: '',
    lon: '',
  },
  sessionLength: '',
  intClocks: [],
  celebrations: [],
  avatarFile: null,
  avatarPreviewUrl: '',
});

const normalizeClockRow = (clock) => ({
  city: (clock?.city || '').trim(),
  timezone: (clock?.timezone || '').trim(),
});

const normalizeCelebrationRow = (celebration) => ({
  date: (celebration?.date || '').trim(),
  title: (celebration?.title || '').trim(),
});

const normalizeSession = (sessionLength) => {
  const clean = String(sessionLength ?? '').trim();
  if (!clean) {
    return '';
  }
  const numeric = Number(clean);
  if (!Number.isFinite(numeric)) {
    return clean;
  }
  return String(Math.trunc(numeric));
};

const normalizeCoord = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return Number(numeric.toFixed(6));
};

const cloneStateForSnapshot = (settings) => ({
  timezone: (settings.timezone || '').trim(),
  weather: {
    cityQuery: settings.weather?.cityQuery || '',
    cityLabel: settings.weather?.cityLabel || '',
    lat: settings.weather?.lat ?? '',
    lon: settings.weather?.lon ?? '',
  },
  sessionLength: normalizeSession(settings.sessionLength),
  intClocks: (settings.intClocks || []).map(normalizeClockRow),
  celebrations: (settings.celebrations || []).map(normalizeCelebrationRow),
  avatarFile: null,
  avatarPreviewUrl: settings.avatarPreviewUrl || '',
});

const areRowsEqual = (first, second) => JSON.stringify(first) === JSON.stringify(second);

const getChangedSections = (settings, initialState) => {
  const normalizedCurrentClocks = (settings.intClocks || []).map(normalizeClockRow);
  const normalizedInitialClocks = (initialState.intClocks || []).map(normalizeClockRow);
  const normalizedCurrentCelebrations = (settings.celebrations || []).map(normalizeCelebrationRow);
  const normalizedInitialCelebrations = (initialState.celebrations || []).map(normalizeCelebrationRow);

  return {
    timezone: (settings.timezone || '').trim() !== (initialState.timezone || '').trim(),
    weather:
      normalizeCoord(settings.weather?.lat) !== normalizeCoord(initialState.weather?.lat) ||
      normalizeCoord(settings.weather?.lon) !== normalizeCoord(initialState.weather?.lon),
    sessionLength: normalizeSession(settings.sessionLength) !== normalizeSession(initialState.sessionLength),
    intClocks: !areRowsEqual(normalizedCurrentClocks, normalizedInitialClocks),
    celebrations: !areRowsEqual(normalizedCurrentCelebrations, normalizedInitialCelebrations),
    avatar: Boolean(settings.avatarFile),
  };
};

const moveRow = (rows, index, delta) => {
  const targetIndex = index + delta;
  if (targetIndex < 0 || targetIndex >= rows.length) {
    return rows;
  }
  const reordered = [...rows];
  const [item] = reordered.splice(index, 1);
  reordered.splice(targetIndex, 0, item);
  return reordered;
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

function Settings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(createDefaultSettingsState());
  const [initialState, setInitialState] = useState(createDefaultSettingsState());
  const [validation, setValidation] = useState(createEmptyValidationState());
  const [showValidation, setShowValidation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchingCity, setSearchingCity] = useState(false);
  const [cityResults, setCityResults] = useState([]);
  const [citySearchError, setCitySearchError] = useState('');
  const [saveSummary, setSaveSummary] = useState(null);
  const [avatarCropSource, setAvatarCropSource] = useState(null);

  const changedSections = useMemo(
    () => getChangedSections(settings, initialState),
    [settings, initialState]
  );
  const hasChanges = useMemo(
    () => Object.values(changedSections).some((value) => value),
    [changedSections]
  );
  const isAvatarCropPending = Boolean(avatarCropSource);

  useEffect(() => {
    document.title = 'Settings';
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadSettings = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        const { userInfo, avatarPreviewUrl } = await fetchUserSettings(token);
        if (!isMounted) {
          return;
        }

        const clocksRaw = Array.isArray(userInfo.intclocks)
          ? userInfo.intclocks
          : Array.isArray(userInfo.intClocks)
          ? userInfo.intClocks
          : [];
        const celebrationsRaw = Array.isArray(userInfo.celebrations) ? userInfo.celebrations : [];

        const lat = userInfo.lat ?? '';
        const lon = userInfo.lon ?? '';
        const hasSavedCoords = Number.isFinite(Number(lat)) && Number.isFinite(Number(lon));
        const cityLabel = hasSavedCoords ? `Saved coordinates (${lat}, ${lon})` : '';

        const hydratedState = {
          timezone: userInfo.timezone || '',
          weather: {
            cityQuery: cityLabel,
            cityLabel,
            lat,
            lon,
          },
          sessionLength: userInfo.sessionLength !== null && userInfo.sessionLength !== undefined
            ? String(userInfo.sessionLength)
            : '',
          intClocks: clocksRaw.map(normalizeClockRow),
          celebrations: celebrationsRaw.map(normalizeCelebrationRow),
          avatarFile: null,
          avatarPreviewUrl: avatarPreviewUrl || localStorage.getItem('profilePic') || '',
        };

        setSettings(hydratedState);
        setInitialState(cloneStateForSnapshot(hydratedState));
        setValidation(createEmptyValidationState());
        setShowValidation(false);
        setSaveSummary(null);
        setAvatarCropSource(null);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setSaveSummary({
          variant: 'danger',
          message: error.message || 'Failed to load settings.',
          sections: [],
        });
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const handleCitySearch = async () => {
    const query = (settings.weather.cityQuery || '').trim();
    if (!query) {
      setCityResults([]);
      setCitySearchError('Enter a city name to search.');
      return;
    }

    try {
      setSearchingCity(true);
      setCitySearchError('');
      const results = await searchCities(query);
      setCityResults(results);
      if (!results.length) {
        setCitySearchError('No matching city found.');
      }
    } catch (error) {
      setCityResults([]);
      setCitySearchError(error.message || 'City search failed.');
    } finally {
      setSearchingCity(false);
    }
  };

  const handleSelectCity = (city) => {
    setSettings((previous) => ({
      ...previous,
      weather: {
        ...previous.weather,
        cityQuery: city.label,
        cityLabel: city.label,
        lat: city.lat,
        lon: city.lon,
      },
    }));
    setCityResults([]);
    setCitySearchError('');
  };

  const handleAvatarChange = async (event) => {
    const inputElement = event.target;
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    try {
      const previewUrl = await fileToDataUrl(file);
      setAvatarCropSource({
        sourceUrl: previewUrl,
        fileName: file.name,
        mimeType: file.type,
      });
      setSaveSummary(null);
    } catch (error) {
      setSaveSummary({
        variant: 'danger',
        message: 'Failed to read avatar file.',
        sections: [],
      });
      setAvatarCropSource(null);
    } finally {
      inputElement.value = '';
    }
  };

  const handleAvatarCropApply = (croppedFile, croppedPreviewUrl) => {
    setSettings((previous) => ({
      ...previous,
      avatarFile: croppedFile,
      avatarPreviewUrl: croppedPreviewUrl,
    }));
    setAvatarCropSource(null);
    setSaveSummary(null);
  };

  const handleAvatarCropCancel = () => {
    setAvatarCropSource(null);
  };

  const handleSaveAll = async () => {
    if (isAvatarCropPending) {
      setSaveSummary({
        variant: 'warning',
        message: 'Apply or cancel the avatar crop before saving.',
        sections: [],
      });
      return;
    }

    const currentValidation = validateSettings(settings);
    setValidation(currentValidation);
    setShowValidation(true);

    if (!currentValidation.isValid) {
      setSaveSummary({
        variant: 'danger',
        message: 'Please fix validation errors before saving.',
        sections: [],
      });
      return;
    }

    if (!hasChanges) {
      setSaveSummary({
        variant: 'success',
        message: 'No changes to save.',
        sections: [],
      });
      return;
    }

    let token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    setSaving(true);
    setSaveSummary(null);

    const sectionResults = [];
    const updatedInitialState = cloneStateForSnapshot(initialState);
    const nextSettings = { ...settings };

    const runSectionSave = async (sectionKey, execute, onSuccess) => {
      try {
        const responsePayload = await execute();
        onSuccess(responsePayload);
        sectionResults.push({
          key: sectionKey,
          status: 'success',
          message: 'Saved',
        });
      } catch (error) {
        sectionResults.push({
          key: sectionKey,
          status: 'failed',
          message: error.message || 'Failed to save',
        });
      }
    };

    if (changedSections.timezone) {
      await runSectionSave(
        'timezone',
        () => saveTimezone(token, settings.timezone.trim()),
        (responsePayload) => {
          if (responsePayload && responsePayload.token) {
            token = responsePayload.token;
            localStorage.setItem('token', token);
          }
          const timezone = settings.timezone.trim();
          updatedInitialState.timezone = timezone;
          nextSettings.timezone = timezone;
          localStorage.setItem('timezone', timezone);
        }
      );
    }

    if (changedSections.weather) {
      await runSectionSave(
        'weather',
        () =>
          saveWeatherLocation(
            token,
            Number(settings.weather.lat),
            Number(settings.weather.lon)
          ),
        () => {
          const lat = Number(settings.weather.lat);
          const lon = Number(settings.weather.lon);
          const normalizedWeather = {
            cityQuery: settings.weather.cityQuery,
            cityLabel: settings.weather.cityLabel,
            lat,
            lon,
          };
          updatedInitialState.weather = normalizedWeather;
          nextSettings.weather = normalizedWeather;
          localStorage.setItem('lat', String(lat));
          localStorage.setItem('lon', String(lon));
        }
      );
    }

    if (changedSections.sessionLength) {
      await runSectionSave(
        'sessionLength',
        () => saveSessionLength(token, Number(settings.sessionLength)),
        () => {
          const sessionLength = normalizeSession(settings.sessionLength);
          updatedInitialState.sessionLength = sessionLength;
          nextSettings.sessionLength = sessionLength;
          localStorage.setItem('sessionLength', sessionLength);
        }
      );
    }

    if (changedSections.intClocks) {
      const clocksPayload = settings.intClocks.map(normalizeClockRow);
      await runSectionSave(
        'intClocks',
        () => saveIntClocks(token, clocksPayload),
        () => {
          updatedInitialState.intClocks = clocksPayload;
          nextSettings.intClocks = clocksPayload;
        }
      );
    }

    if (changedSections.celebrations) {
      const celebrationsPayload = settings.celebrations.map(normalizeCelebrationRow);
      await runSectionSave(
        'celebrations',
        () => saveCelebrations(token, celebrationsPayload),
        () => {
          updatedInitialState.celebrations = celebrationsPayload;
          nextSettings.celebrations = celebrationsPayload;
        }
      );
    }

    if (changedSections.avatar && settings.avatarFile) {
      await runSectionSave(
        'avatar',
        () => saveAvatar(token, settings.avatarFile),
        () => {
          updatedInitialState.avatarPreviewUrl = settings.avatarPreviewUrl || '';
          nextSettings.avatarFile = null;
          if (settings.avatarPreviewUrl) {
            localStorage.setItem('profilePic', settings.avatarPreviewUrl);
          }
        }
      );
    }

    setInitialState(updatedInitialState);
    setSettings(nextSettings);

    const failedCount = sectionResults.filter((result) => result.status === 'failed').length;
    const successCount = sectionResults.filter((result) => result.status === 'success').length;

    let variant = 'success';
    let message = 'All changed settings were saved successfully.';
    if (failedCount > 0 && successCount > 0) {
      variant = 'warning';
      message = 'Some sections failed to save. Please review the details below.';
    } else if (failedCount > 0) {
      variant = 'danger';
      message = 'Failed to save settings.';
    }

    setSaveSummary({
      variant,
      message,
      sections: sectionResults,
    });
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="AppRootDesktop">
        <div className="AppRootInnerOverlay">
          <div className="settings-page">
            <Container fluid="lg" className="settings-container py-5 text-center">
              <Spinner animation="border" role="status" />
            </Container>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="AppRootDesktop">
      <div className="AppRootInnerOverlay">
        <div className="settings-page edit-page">
          <div className="action-bar">
            <div className="action-bar-heading">
              <h1>Settings</h1>
              <div className="action-bar-subtitle">
                Update profile, timezone, weather, clocks, and celebrations.
              </div>
            </div>
            <div className="settings-action-controls">
              <Badge
                pill
                bg={hasChanges ? 'warning' : 'success'}
                text={hasChanges ? 'dark' : 'light'}
                className="settings-status-badge"
              >
                {hasChanges ? 'Unsaved changes' : 'All changes saved'}
              </Badge>
              <Button
                variant="success"
                className="settings-save-btn"
                disabled={!hasChanges || saving || isAvatarCropPending}
                onClick={handleSaveAll}
              >
                {saving && <Spinner animation="border" size="sm" />}
                {!saving && 'Save All'}
                {saving && 'Saving...'}
              </Button>
            </div>
          </div>

          <Container fluid="lg" className="settings-container">
            {saveSummary && (
              <Alert variant={saveSummary.variant} className="settings-alert">
                <div>{saveSummary.message}</div>
                {saveSummary.sections && saveSummary.sections.length > 0 && (
                  <ul className="settings-alert-list">
                    {saveSummary.sections.map((section) => (
                      <li key={section.key}>
                        {SECTION_LABELS[section.key] || section.key}: {section.status === 'success' ? 'Saved' : section.message}
                      </li>
                    ))}
                  </ul>
                )}
              </Alert>
            )}

            <div className="task-card settings-card">
              <div className="settings-card-header">
                <div>
                  <h2 className="settings-card-title">Profile</h2>
                  <p className="settings-card-subtitle">Upload, crop (1:1), and save a profile avatar.</p>
                </div>
              </div>

              <div className="settings-avatar-row">
                <div className="settings-avatar-preview">
                  {settings.avatarPreviewUrl ? (
                    <img src={settings.avatarPreviewUrl} alt="Avatar preview" />
                  ) : (
                    <div className="settings-avatar-placeholder">No avatar selected</div>
                  )}
                </div>
                <div>
                  <Form.Group controlId="settings-avatar">
                    <Form.Label>Avatar image</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleAvatarChange}
                    />
                  </Form.Group>
                  <div className="settings-help">Accepted formats: JPEG, PNG, WEBP. Max file size: 2MB.</div>
                  {isAvatarCropPending && (
                    <div className="settings-help">Avatar crop is pending. Apply or cancel before saving.</div>
                  )}
                  {showValidation && validation.errors.avatar && (
                    <div className="settings-error-text">{validation.errors.avatar}</div>
                  )}
                </div>
              </div>

              {avatarCropSource && (
                <AvatarCropper
                  sourceUrl={avatarCropSource.sourceUrl}
                  sourceFileName={avatarCropSource.fileName}
                  sourceMimeType={avatarCropSource.mimeType}
                  onApply={handleAvatarCropApply}
                  onCancel={handleAvatarCropCancel}
                />
              )}
            </div>

            <div className="task-card settings-card">
              <div className="settings-card-header">
                <div>
                  <h2 className="settings-card-title">Time and Weather</h2>
                  <p className="settings-card-subtitle">Choose a timezone and weather location.</p>
                </div>
              </div>

              <Row className="g-3">
                <Col xs={12} md={6}>
                  <Form.Group controlId="settings-timezone">
                    <Form.Label>Timezone</Form.Label>
                    <Form.Select
                      value={settings.timezone}
                      onChange={(event) =>
                        setSettings((previous) => ({
                          ...previous,
                          timezone: event.target.value,
                        }))
                      }
                    >
                      <option value="">Select timezone</option>
                      {TIMEZONE_OPTIONS.map((timezone) => (
                        <option key={timezone} value={timezone}>
                          {timezone}
                        </option>
                      ))}
                    </Form.Select>
                    {showValidation && validation.errors.timezone && (
                      <div className="settings-error-text">{validation.errors.timezone}</div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group controlId="settings-city-search">
                <Form.Label>Weather city search</Form.Label>
                <div className="settings-weather-search">
                  <Form.Control
                    type="text"
                    value={settings.weather.cityQuery}
                    placeholder="Search city, region, or country"
                    onChange={(event) =>
                      setSettings((previous) => ({
                        ...previous,
                        weather: {
                          ...previous.weather,
                          cityQuery: event.target.value,
                        },
                      }))
                    }
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleCitySearch();
                      }
                    }}
                  />
                  <Button variant="primary" onClick={handleCitySearch} disabled={searchingCity}>
                    {searchingCity ? <Spinner animation="border" size="sm" /> : <FaSearch />}
                    {searchingCity ? 'Searching' : 'Search'}
                  </Button>
                </div>
                {citySearchError && <div className="settings-error-text">{citySearchError}</div>}
              </Form.Group>

              {cityResults.length > 0 && (
                <div className="settings-city-results">
                  {cityResults.map((city) => (
                    <button
                      type="button"
                      key={city.id}
                      className="settings-city-result"
                      onClick={() => handleSelectCity(city)}
                    >
                      {city.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="settings-selected-city">
                Selected location: {settings.weather.cityLabel || 'No city selected'}
              </div>

              <Row className="g-3 mt-1">
                <Col xs={12} md={6}>
                  <Form.Group controlId="settings-latitude">
                    <Form.Label>Latitude</Form.Label>
                    <Form.Control type="text" value={settings.weather.lat} readOnly />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="settings-longitude">
                    <Form.Label>Longitude</Form.Label>
                    <Form.Control type="text" value={settings.weather.lon} readOnly />
                  </Form.Group>
                </Col>
              </Row>

              {showValidation && validation.errors.weather && (
                <div className="settings-error-text">{validation.errors.weather}</div>
              )}
            </div>

            <div className="task-card settings-card">
              <div className="settings-card-header">
                <div>
                  <h2 className="settings-card-title">Session</h2>
                  <p className="settings-card-subtitle">Set default session length in minutes.</p>
                </div>
              </div>
              <Row>
                <Col xs={12} md={5}>
                  <Form.Group controlId="settings-session-length">
                    <Form.Label>Session length (minutes)</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      step="1"
                      placeholder="e.g. 25"
                      value={settings.sessionLength}
                      onChange={(event) =>
                        setSettings((previous) => ({
                          ...previous,
                          sessionLength: event.target.value,
                        }))
                      }
                    />
                    {showValidation && validation.errors.sessionLength && (
                      <div className="settings-error-text">{validation.errors.sessionLength}</div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="task-card settings-card">
              <div className="settings-card-header">
                <div>
                  <h2 className="settings-card-title">International Clocks</h2>
                  <p className="settings-card-subtitle">Manage additional city clocks shown in the app.</p>
                </div>
              </div>

              {settings.intClocks.length === 0 && (
                <div className="settings-empty-text">No clocks configured yet.</div>
              )}

              <Row className="g-3">
                {settings.intClocks.map((clock, index) => (
                  <Col xs={12} key={`clock-${index}`}>
                    <div className="settings-row-card">
                      <Row className="g-3 align-items-end">
                        <Col xs={12} md={4}>
                          <Form.Group controlId={`clock-city-${index}`}>
                            <Form.Label>City</Form.Label>
                            <Form.Control
                              type="text"
                              value={clock.city}
                              onChange={(event) =>
                                setSettings((previous) => {
                                  const nextRows = [...previous.intClocks];
                                  nextRows[index] = {
                                    ...nextRows[index],
                                    city: event.target.value,
                                  };
                                  return {
                                    ...previous,
                                    intClocks: nextRows,
                                  };
                                })
                              }
                            />
                            {showValidation && validation.errors.intClocks[index]?.city && (
                              <div className="settings-error-text">{validation.errors.intClocks[index].city}</div>
                            )}
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={5}>
                          <Form.Group controlId={`clock-timezone-${index}`}>
                            <Form.Label>Timezone</Form.Label>
                            <Form.Select
                              value={clock.timezone}
                              onChange={(event) =>
                                setSettings((previous) => {
                                  const nextRows = [...previous.intClocks];
                                  nextRows[index] = {
                                    ...nextRows[index],
                                    timezone: event.target.value,
                                  };
                                  return {
                                    ...previous,
                                    intClocks: nextRows,
                                  };
                                })
                              }
                            >
                              <option value="">Select timezone</option>
                              {TIMEZONE_OPTIONS.map((timezone) => (
                                <option key={`${timezone}-${index}`} value={timezone}>
                                  {timezone}
                                </option>
                              ))}
                            </Form.Select>
                            {showValidation && validation.errors.intClocks[index]?.timezone && (
                              <div className="settings-error-text">{validation.errors.intClocks[index].timezone}</div>
                            )}
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={3}>
                          <div className="settings-row-actions">
                            <Button
                              variant="outline-light"
                              onClick={() =>
                                setSettings((previous) => ({
                                  ...previous,
                                  intClocks: moveRow(previous.intClocks, index, -1),
                                }))
                              }
                              disabled={index === 0}
                              title="Move up"
                            >
                              <FaArrowUp />
                            </Button>
                            <Button
                              variant="outline-light"
                              onClick={() =>
                                setSettings((previous) => ({
                                  ...previous,
                                  intClocks: moveRow(previous.intClocks, index, 1),
                                }))
                              }
                              disabled={index === settings.intClocks.length - 1}
                              title="Move down"
                            >
                              <FaArrowDown />
                            </Button>
                            <Button
                              variant="outline-danger"
                              onClick={() =>
                                setSettings((previous) => ({
                                  ...previous,
                                  intClocks: previous.intClocks.filter((_, rowIndex) => rowIndex !== index),
                                }))
                              }
                              title="Delete"
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                ))}
              </Row>

              <div className="settings-list-toolbar">
                <Button
                  variant="outline-primary"
                  onClick={() =>
                    setSettings((previous) => ({
                      ...previous,
                      intClocks: [...previous.intClocks, { city: '', timezone: '' }],
                    }))
                  }
                >
                  <FaPlus /> Add clock
                </Button>
              </div>
            </div>

            <div className="task-card settings-card">
              <div className="settings-card-header">
                <div>
                  <h2 className="settings-card-title">Celebrations</h2>
                  <p className="settings-card-subtitle">Track recurring dates in DD/MM format.</p>
                </div>
              </div>

              {settings.celebrations.length === 0 && (
                <div className="settings-empty-text">No celebrations configured yet.</div>
              )}

              <Row className="g-3">
                {settings.celebrations.map((celebration, index) => (
                  <Col xs={12} key={`celebration-${index}`}>
                    <div className="settings-row-card">
                      <Row className="g-3 align-items-end">
                        <Col xs={12} md={3}>
                          <Form.Group controlId={`celebration-date-${index}`}>
                            <Form.Label>Date (DD/MM)</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="DD/MM"
                              value={celebration.date}
                              onChange={(event) =>
                                setSettings((previous) => {
                                  const nextRows = [...previous.celebrations];
                                  nextRows[index] = {
                                    ...nextRows[index],
                                    date: event.target.value,
                                  };
                                  return {
                                    ...previous,
                                    celebrations: nextRows,
                                  };
                                })
                              }
                            />
                            {showValidation && validation.errors.celebrations[index]?.date && (
                              <div className="settings-error-text">{validation.errors.celebrations[index].date}</div>
                            )}
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                          <Form.Group controlId={`celebration-title-${index}`}>
                            <Form.Label>Title</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Event title"
                              value={celebration.title}
                              onChange={(event) =>
                                setSettings((previous) => {
                                  const nextRows = [...previous.celebrations];
                                  nextRows[index] = {
                                    ...nextRows[index],
                                    title: event.target.value,
                                  };
                                  return {
                                    ...previous,
                                    celebrations: nextRows,
                                  };
                                })
                              }
                            />
                            {showValidation && validation.errors.celebrations[index]?.title && (
                              <div className="settings-error-text">{validation.errors.celebrations[index].title}</div>
                            )}
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={3}>
                          <div className="settings-row-actions">
                            <Button
                              variant="outline-light"
                              onClick={() =>
                                setSettings((previous) => ({
                                  ...previous,
                                  celebrations: moveRow(previous.celebrations, index, -1),
                                }))
                              }
                              disabled={index === 0}
                              title="Move up"
                            >
                              <FaArrowUp />
                            </Button>
                            <Button
                              variant="outline-light"
                              onClick={() =>
                                setSettings((previous) => ({
                                  ...previous,
                                  celebrations: moveRow(previous.celebrations, index, 1),
                                }))
                              }
                              disabled={index === settings.celebrations.length - 1}
                              title="Move down"
                            >
                              <FaArrowDown />
                            </Button>
                            <Button
                              variant="outline-danger"
                              onClick={() =>
                                setSettings((previous) => ({
                                  ...previous,
                                  celebrations: previous.celebrations.filter((_, rowIndex) => rowIndex !== index),
                                }))
                              }
                              title="Delete"
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                ))}
              </Row>

              <div className="settings-list-toolbar">
                <Button
                  variant="outline-primary"
                  onClick={() =>
                    setSettings((previous) => ({
                      ...previous,
                      celebrations: [...previous.celebrations, { date: '', title: '' }],
                    }))
                  }
                >
                  <FaPlus /> Add celebration
                </Button>
              </div>
            </div>
          </Container>
        </div>
      </div>
    </div>
  );
}

export default Settings;
